import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useGetCurrentProjectMemberQuery } from '../services/teamApi';
import type { ProjectMember, ProjectMemberRole } from '../types';

interface UseProjectMemberResult {
  /** The current user's project member record */
  member: ProjectMember | null;
  /** The user's role in the project */
  role: ProjectMemberRole | null;
  /** Array of permissions the user has in this project */
  permissions: string[];
  /** Check if user has a specific permission */
  hasPermission: (permission: string) => boolean;
  /** Check if user has any of the specified permissions */
  hasAnyPermission: (permissions: string[]) => boolean;
  /** Check if user has all of the specified permissions */
  hasAllPermissions: (permissions: string[]) => boolean;
  /** Whether the member data is still loading */
  isLoading: boolean;
  /** Whether there was an error loading the member data */
  isError: boolean;
  /** Whether the user is a member of this project */
  isMember: boolean;
}

/**
 * Hook to get the current user's project member information
 *
 * @param projectId - The project ID to get member info for
 * @returns Object containing member info, permissions, and helper functions
 *
 * @example
 * ```tsx
 * const { member, role, hasPermission, isLoading } = useProjectMember(projectId);
 *
 * if (isLoading) return <Spinner />;
 *
 * if (hasPermission('manage_team')) {
 *   // Show team management UI
 * }
 * ```
 */
export const useProjectMember = (projectId?: string): UseProjectMemberResult => {
  const { user, isAuthenticated } = useAuth();

  const { data, isLoading, isError } = useGetCurrentProjectMemberQuery(
    { projectId: projectId! },
    { skip: !projectId || !user?.id || !isAuthenticated }
  );

  const member = useMemo((): ProjectMember | null => {
    if (!data?.success || !data.data) return null;
    return data.data;
  }, [data]);

  const role = useMemo((): ProjectMemberRole | null => {
    return member?.role || null;
  }, [member]);

  const permissions = useMemo((): string[] => {
    return member?.permissions || [];
  }, [member]);

  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(p => permissions.includes(p));
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(p => permissions.includes(p));
  };

  const isMember = useMemo((): boolean => {
    return !!member && member.status === 'active';
  }, [member]);

  return {
    member,
    role,
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    isError,
    isMember,
  };
};

export default useProjectMember;
